import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { Image } from 'antd';
import { XcIcon } from '../../XcIcon';
import zipIcon from '../../../assets/file-zip.svg';
import { useObjectURL } from '../hooks/useObjectURL';
import { FooterReportChip } from './FooterReportChip';
import type { FooterReport, FooterFileUrl } from '../types';

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  /** 当前本地文件的上传进度，按 File 对象引用匹配，值为 0–100。 */
  uploadProgress?: ReadonlyMap<File, number>;
  /** 粘贴报告链接生成的报告 chip（与文件 chip 同处一行，按进入列表的顺序渲染）。 */
  reports?: FooterReport[];
  /** 移除某个报告 chip（按 id）。 */
  onReportRemove?: (id: string) => void;
  /** 已上传文件（有 OSS URL、无需再上传）卡片列表，与文件 chip 同款样式。 */
  fileUrls?: FooterFileUrl[];
  /** 移除某个已上传文件卡片（按 id）。 */
  onFileUrlRemove?: (id: string) => void;
};

type FooterDisplayItem =
  | { type: 'report'; key: string; report: FooterReport }
  | { type: 'file'; key: string; file: File; index: number }
  | { type: 'fileUrl'; key: string; fileUrl: FooterFileUrl };

/** 文件大小格式化：<1KB 显示 B，<1MB 显示 KB（取整），否则 MB（保留 1 位）。 */
const formatSize = (size: number): string => {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
};

/** 文件名是否图片扩展名。 */
const isImageName = (name: string): boolean =>
  /\.(png|jpe?g|gif|webp|bmp|svg|dib|tiff?|ico|icns|sgi|avif|heif|pic|j2c|j2k|jp2|jpc|jpf|jpx)$/i
    .test(name || '');

/** 是否图片：优先看 MIME，兜底看扩展名（粘贴/拖拽来的 File 可能无 type）。 */
const isImageFile = (file: File): boolean =>
  (file.type?.startsWith('image/') ?? false) || isImageName(file.name || '');

/** 文件名是否视频扩展名。 */
const isVideoName = (name: string): boolean => /\.(mp4|mov|webm|mkv|avi|m4v|ogg)$/i.test(name || '');

/** 是否视频：优先看 MIME，兜底看扩展名。 */
const isVideoFile = (file: File): boolean =>
  (file.type?.startsWith('video/') ?? false) || isVideoName(file.name || '');

const loadVideoSafely = (video: HTMLVideoElement): void => {
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return;
  try {
    video.load();
  } catch {
    // jsdom does not implement media loading; browsers do.
  }
};

const createVideoThumbnail = (src: string): Promise<string> =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    let frameCallbackId: number | null = null;
    const timer = window.setTimeout(() => finish(''), 8000);
    const cleanup = () => {
      window.clearTimeout(timer);
      if (frameCallbackId != null && 'cancelVideoFrameCallback' in video) {
        video.cancelVideoFrameCallback(frameCallbackId);
      }
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.oncanplay = null;
      video.onseeked = null;
      video.ontimeupdate = null;
      video.onerror = null;
      video.pause();
    };
    const finish = (thumbnail: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(thumbnail);
    };
    const capture = () => {
      try {
        // 缩略图只在小尺寸展示，若按视频原分辨率（可达 1080p/4K）建 canvas，
        // 同步的 toDataURL 会阻塞主线程数秒（尤其配合弹窗关闭动画会明显卡死）。
        // 这里等比缩到最长边 MAX_THUMB_EDGE，编码成本降到毫秒级。
        const MAX_THUMB_EDGE = 160;
        const vw = video.videoWidth || 32;
        const vh = video.videoHeight || 32;
        const scale = Math.min(1, MAX_THUMB_EDGE / Math.max(vw, vh));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(vw * scale));
        canvas.height = Math.max(1, Math.round(vh * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx || canvas.width <= 0 || canvas.height <= 0) {
          finish('');
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.72));
      } catch {
        finish('');
      }
    };
    const captureWhenFrameReady = () => {
      if ('requestVideoFrameCallback' in video) {
        frameCallbackId = video.requestVideoFrameCallback(() => capture());
        return;
      }
      window.requestAnimationFrame(capture);
    };
    const decodeFirstFrame = () => {
      const seekTime = Number.isFinite(video.duration) && video.duration > 0.2
        ? Math.min(0.1, video.duration / 2)
        : 0;
      video.onseeked = captureWhenFrameReady;
      if (seekTime > 0) {
        try {
          video.currentTime = seekTime;
          return;
        } catch {
          // Fall through to play-based decoding.
        }
      }

      video.ontimeupdate = captureWhenFrameReady;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => captureWhenFrameReady());
      }
    };

    video.onloadedmetadata = decodeFirstFrame;
    video.oncanplay = decodeFirstFrame;
    video.onerror = () => finish('');

    // 跨域视频（如资产库 OSS 地址）截帧必须带 crossOrigin，否则 canvas 会被污染、
    // toDataURL 抛错，只能回退到无首帧的占位图。OSS 已配置 CORS，匿名请求即可成功；
    // 对同源 blob / 无 CORS 的地址也无副作用（与 nodes/video/hooks.ts 的视频节点截帧保持一致）。
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.src = src;
    loadVideoSafely(video);
  });

type VideoThumbnailState = {
  src: string;
  status: 'loading' | 'ready' | 'failed';
};

const useVideoThumbnail = (src: string): VideoThumbnailState => {
  const [state, setState] = useState<VideoThumbnailState>({ src: '', status: 'loading' });

  useEffect(() => {
    if (!src) {
      setState({ src: '', status: 'failed' });
      return;
    }

    let alive = true;
    setState({ src: '', status: 'loading' });
    createVideoThumbnail(src).then((nextThumbnail) => {
      if (!alive) return;
      setState(nextThumbnail
        ? { src: nextThumbnail, status: 'ready' }
        : { src: '', status: 'failed' });
    });

    return () => {
      alive = false;
    };
  }, [src]);

  return state;
};

const VideoThumbnail: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const thumbnail = useVideoThumbnail(src);

  if (thumbnail.status === 'ready') {
    return (
      <span className="rte-footer-video-thumb-wrap">
        <img className="rte-footer-file-thumb rte-footer-video-thumb" src={thumbnail.src} alt={alt} />
        <span className="rte-footer-video-play-icon" />
      </span>
    );
  }

  return (
    <span className="rte-footer-video-thumb-wrap rte-footer-video-thumb-fallback">
      <span className="rte-footer-video-play-icon" />
    </span>
  );
};

/** 扩展名 → 全局 iconfont symbol id，用于非图片文件的类型图标。 */
const fileIconId = (name: string): string => {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (['xls', 'xla', 'xlb', 'xlc', 'xlm', 'xlsx', 'xlt', 'xlw', 'csv', 'tsv', 'iif'].includes(ext)) return 'icon-a-xlsxcsv';
  if (['doc', 'docx', 'dot', 'odt', 'rtf', 'wiz'].includes(ext)) return 'icon-docx';
  if (['ppt', 'pptx', 'pot', 'ppa', 'pps', 'pwz'].includes(ext)) return 'icon-ppt';
  if (['txt', 'asm', 'bat', 'c', 'cc', 'conf', 'cpp', 'css', 'cxx', 'def', 'dic', 'eml', 'h', 'hh', 'in', 'js', 'ksh', 'php', 'pkl', 'rb', 'tar', 'tex', 'ts', 'xml'].includes(ext)) return 'icon-txt';
  if (ext === 'pdf') return 'icon-pdf';
  if (['html', 'htm', 'json', 'ics', 'ifb'].includes(ext)) return 'icon-a-htmljson';
  if (ext === 'md') return 'icon-md';
  if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v'].includes(ext)) return 'icon-shipin';
  if (['xmind', 'mm'].includes(ext)) return 'icon-xmind';
  // 浏览器解不了的图片格式（缩略图加载失败后回落到这里），用通用文件图标而非链接图标。
  if (['tiff', 'tif', 'icns', 'sgi', 'heif', 'dib', 'pic', 'j2c', 'j2k', 'jp2', 'jpc', 'jpf', 'jpx'].includes(ext)) {
    return 'icon-txt';
  }
  return 'icon-lianjie';
};

const FooterFileTypeIcon: React.FC<{ fileName: string }> = ({ fileName }) => (
  <div className="rte-footer-file-icon">
    {/\.zip$/i.test(fileName) ? (
      <img className="rte-footer-file-zip-icon" src={zipIcon} alt="" aria-hidden="true" />
    ) : (
      <XcIcon name={fileIconId(fileName)} size={18} aria-hidden="true" />
    )}
  </div>
);

const FILE_PROGRESS_RING_RADIUS = 6.5;
const FILE_PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * FILE_PROGRESS_RING_RADIUS;

const normalizeUploadProgress = (progress: number): number => {
  if (!Number.isFinite(progress)) return 0;
  return Math.min(Math.max(progress, 0), 100);
};

const FooterFileProgress: React.FC<{ fileName: string; progress: number }> = ({ fileName, progress }) => {
  const normalizedProgress = normalizeUploadProgress(progress);
  const dashOffset = FILE_PROGRESS_RING_CIRCUMFERENCE * (1 - normalizedProgress / 100);

  return (
    <span className="rte-footer-file-loading-mask">
      <svg
        className="rte-footer-file-progress-ring"
        viewBox="0 0 16 16"
        width="16"
        height="16"
        role="progressbar"
        aria-label={`${fileName} 上传进度`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={normalizedProgress}
      >
        <circle
          cx="8"
          cy="8"
          r={FILE_PROGRESS_RING_RADIUS}
          fill="none"
          stroke="rgba(255, 255, 255, 0.5)"
          strokeWidth="1.5"
        />
        <circle
          className="rte-footer-file-progress-value"
          cx="8"
          cy="8"
          r={FILE_PROGRESS_RING_RADIUS}
          fill="none"
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={FILE_PROGRESS_RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 8 8)"
        />
      </svg>
    </span>
  );
};

/** 单个文件 chip：图片显示缩略图（antd Image，点击放大），非图片显示类型图标 + 名称 + 大小。 */
const FooterFileChip: React.FC<{
  file: File;
  onRemove: () => void;
  displayKey?: string;
  uploadProgress?: number;
}> = ({ file, onRemove, displayKey, uploadProgress }) => {
  const isVideo = isVideoFile(file);
  // tiff / icns / sgi / jp2 系 / heif / dib / pic 浏览器解不了，<img> 会渲染成破图 ——
  // 加载失败就回落到类型图标。
  const [thumbFailed, setThumbFailed] = useState(false);
  const isImage = isImageFile(file) && !thumbFailed;
  const uploading = uploadProgress != null;
  // 只有图片/视频需要 objectURL，避免非媒体文件无谓占用。
  const objectUrl = useObjectURL(isImage || isVideo ? file : null);
  return (
    <div
      className={`rte-footer-file-chip${uploading ? ' rte-footer-file-chip-uploading' : ''}`}
      data-rte-footer-display-key={displayKey}
      aria-busy={uploading}
    >
      <div className="rte-footer-file-preview">
        {isImage ? (
          // 预览遮罩必须走 document.body 而非 popupHost——host 是编辑器后代，业务祖先链上的
          // transform/filter 会困住它的 fixed+inset:0，导致遮罩缩水盖不住视口。
          // 这里 getContainer 显式传 document.body：外层 ConfigProvider 把全编辑器的
          // antd 浮层 getPopupContainer 收口到了 popupHost，Image preview 默认会继承该容器，
          // 不显式覆盖会跟技能面板挤在同一棵 host 子树里。
          <Image
            className="rte-footer-file-thumb"
            width={32}
            height={32}
            src={objectUrl}
            alt={file.name}
            onError={() => setThumbFailed(true)}
            preview={uploading ? false : { mask: null, getContainer: () => document.body }}
          />
        ) : isVideo ? (
          <VideoThumbnail src={objectUrl} alt={file.name} />
        ) : (
          <FooterFileTypeIcon fileName={file.name} />
        )}
        {uploading && <FooterFileProgress fileName={file.name} progress={uploadProgress} />}
      </div>
      <div className="rte-footer-file-info">
        <span className="rte-footer-file-name" title={file.name}>{file.name}</span>
        <span className="rte-footer-file-size">{formatSize(file.size)}</span>
      </div>
      <span
        className="rte-footer-file-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <i
          className="iconfont icon-guanbi"
          style={{ width: '8px', height: '8px', fontSize: '8px', display: 'flex', alignItems: 'center' }}
        />
      </span>
    </div>
  );
};

/** 已上传文件 chip（有 OSS URL、无需重传）：与 FooterFileChip 同款样式，
 *  图片走 <Image> 缩略图预览，非图片按扩展名给类型图标；大小是业务侧格式化好的字符串。 */
const FooterFileUrlChip: React.FC<{ file: FooterFileUrl; onRemove: () => void; displayKey?: string }> = ({ file, onRemove, displayKey }) => {
  const isImage = isImageName(file.name);
  const isVideo = isVideoName(file.name) || isVideoName(file.url);
  const thumbnailUrl = file.thumbnailUrl || file.thumbnail;
  return (
    <div className="rte-footer-file-chip" data-rte-footer-display-key={displayKey}>
      {thumbnailUrl ? (
        <img className="rte-footer-file-thumb" src={thumbnailUrl} alt={file.name} />
      ) : isImage ? (
        <Image
          className="rte-footer-file-thumb"
          width={32}
          height={32}
          src={file.url}
          alt={file.name}
          preview={{ mask: null, getContainer: () => document.body }}
        />
      ) : isVideo ? (
        <VideoThumbnail src={file.url} alt={file.name} />
      ) : (
        <FooterFileTypeIcon fileName={file.name} />
      )}
      <div className="rte-footer-file-info">
        <span className="rte-footer-file-name" title={file.name}>{file.name}</span>
        {file.size ? <span className="rte-footer-file-size">{file.size}</span> : null}
      </div>
      <span
        className="rte-footer-file-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <i
          className="iconfont icon-guanbi"
          style={{ width: '8px', height: '8px', fontSize: '8px', display: 'flex', alignItems: 'center' }}
        />
      </span>
    </div>
  );
};

/** footer 上传文件列表：在编辑区上方独立整行（对齐 V1 位置），溢出时两端显示渐变遮罩 + 箭头。
 *  state 住在父组件（index.tsx 的 footerFiles），本组件只渲染 + 回调移除。空列表返回 null。 */
export const FooterFilesList: React.FC<Props> = React.memo(({
  files,
  onChange,
  uploadProgress,
  reports,
  onReportRemove,
  fileUrls,
  onFileUrlRemove,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const displayOrderRef = useRef<string[]>([]);
  const fileKeyCacheRef = useRef<WeakMap<File, string>>(new WeakMap());
  const fileKeySeqRef = useRef(0);
  const pendingScrollKeyRef = useRef<string | null>(null);
  // 横向滚动遮罩显隐：文件溢出时两端显示渐变遮罩 + 箭头。
  const [scroll, setScroll] = useState<{ showLeft: boolean; showRight: boolean }>({
    showLeft: false,
    showRight: false,
  });

  const updateScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setScroll({
      showLeft: el.scrollLeft > 0,
      showRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  // 列表变化后重测溢出；有新增 chip 时自动滚到新 chip。
  useLayoutEffect(() => {
    const targetKey = pendingScrollKeyRef.current;
    if (!targetKey) {
      updateScroll();
      return;
    }

    pendingScrollKeyRef.current = null;
    const targetEl = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-rte-footer-display-key]') ?? [],
    ).find((el) => el.dataset.rteFooterDisplayKey === targetKey);

    if (!targetEl) {
      updateScroll();
      return;
    }

    targetEl.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    window.setTimeout(updateScroll, 300);
  }, [files, reports, fileUrls, updateScroll]);

  const scrollByDir = useCallback(
    (dir: 1 | -1) => {
      const el = listRef.current;
      if (!el) return;
      el.scrollBy({ left: dir * 200, behavior: 'smooth' });
      // 平滑滚动结束后再测一次终态。
      window.setTimeout(updateScroll, 300);
    },
    [updateScroll],
  );

  const remove = useCallback(
    (idx: number) => () => onChange(files.filter((_, i) => i !== idx)),
    [files, onChange],
  );

  const getFileDisplayKey = (file: File): string => {
    const cachedKey = fileKeyCacheRef.current.get(file);
    if (cachedKey) return cachedKey;
    const nextKey = `file:${fileKeySeqRef.current++}`;
    fileKeyCacheRef.current.set(file, nextKey);
    return nextKey;
  };

  const displayItemsByKey = new Map<string, FooterDisplayItem>();

  files.forEach((file, index) => {
    const key = getFileDisplayKey(file);
    displayItemsByKey.set(key, { type: 'file', key, file, index });
  });

  reports?.forEach((report) => {
    const key = `report:${report.id}`;
    displayItemsByKey.set(key, { type: 'report', key, report });
  });

  fileUrls?.forEach((fileUrl) => {
    const key = `fileUrl:${fileUrl.id}`;
    displayItemsByKey.set(key, { type: 'fileUrl', key, fileUrl });
  });

  displayOrderRef.current = displayOrderRef.current.filter((key) => displayItemsByKey.has(key));
  displayItemsByKey.forEach((_, key) => {
    if (displayOrderRef.current.includes(key)) return;
    displayOrderRef.current.push(key);
    pendingScrollKeyRef.current = key;
  });

  const displayItems = displayOrderRef.current
    .map((key) => displayItemsByKey.get(key))
    .filter((item): item is FooterDisplayItem => item != null);

  if (displayItems.length === 0) return null;

  return (
    <div className="rte-footer-files-wrapper">
      {scroll.showLeft && (
        <div className="rte-footer-files-mask rte-footer-files-mask-left">
          <span
            className="rte-footer-files-arrow rte-footer-files-arrow-left"
            onClick={() => scrollByDir(-1)}
          >
            <svg style={{ width: '16px', height: '16px', transform: 'rotate(180deg)' }} aria-hidden="true">
              <use xlinkHref="#icon-fanhuijiantou" />
            </svg>
          </span>
        </div>
      )}
      <div className="rte-footer-files-list" ref={listRef} onScroll={updateScroll}>
        {displayItems.map((item) => {
          if (item.type === 'report') {
            return (
              <FooterReportChip
                key={item.key}
                displayKey={item.key}
                report={item.report}
                onRemove={() => onReportRemove?.(item.report.id)}
              />
            );
          }
          if (item.type === 'fileUrl') {
            return (
              <FooterFileUrlChip
                key={item.key}
                displayKey={item.key}
                file={item.fileUrl}
                onRemove={() => onFileUrlRemove?.(item.fileUrl.id)}
              />
            );
          }
          return (
            <FooterFileChip
              key={item.key}
              displayKey={item.key}
              file={item.file}
              onRemove={remove(item.index)}
              uploadProgress={uploadProgress?.get(item.file)}
            />
          );
        })}
      </div>
      {scroll.showRight && (
        <div className="rte-footer-files-mask rte-footer-files-mask-right">
          <span
            className="rte-footer-files-arrow rte-footer-files-arrow-right"
            onClick={() => scrollByDir(1)}
          >
            <svg style={{ width: '16px', height: '16px' }} aria-hidden="true">
              <use xlinkHref="#icon-fanhuijiantou" />
            </svg>
          </span>
        </div>
      )}
    </div>
  );
});
FooterFilesList.displayName = 'FooterFilesList';
